import {
  $ComissionsState,
  $Comissions,
  fetchComissions,
  removeComission,
} from "@store/internal-study/comissions-store";
import { $UserAddPermissions, ACTIONS } from "@store/user-store";
import { Checkbox, Tooltip } from "@mui/material";
import { ErrorIndicator, Loader } from "@ui/indicators";
import { NotePencilIcon, TrashIcon } from "@phosphor-icons/react";
import React, { useEffect } from "react";
import { ComissionsShortDataT } from "@interfaces/internal-study/comissions";

import { WrapperSVG } from "@components/WrapperSVG/WrapperSVG";
import clsx from "clsx";
import style from "@scss/pages/company/departments.module.scss";
import tableStyle from "@scss/components/tables/base-table.module.scss";
import { useAsyncConfirm } from "@hooks/useAsyncConfirm";
import { useChoiceState } from "@hooks/useChoiceState";
import { useCompanyId } from "@store/company/company-store";
import useModal from "@modals/modal-hook";
import { useUnit } from "effector-react";

const InternalStudyComissionsTable: React.FC = () => {
  const { isLoading, error, isFetched } = useUnit($ComissionsState);
  const comissions = useUnit($Comissions);
  const permissions = useUnit($UserAddPermissions);
  const companyId = useCompanyId();

  const {
    selectedItems,
    areAllItemsSelected,
    selectAll,
    selectById,
    clearSelectedItems,
    getIsItemSelected,
  } = useChoiceState(comissions, (comissions) => comissions.id);

  const { open } = useModal();
  const confirm = useAsyncConfirm();

  const handleRemoveComission = (comission: ComissionsShortDataT) => {
    open("ConfirmActionModal", {
      btnText: "Удалить",
      modalData: {
        text: `Вы уверены, что хотите удалить комиссию "${comission.title}"?`,
      },
      onConfirm: () => {
        removeComission({ id: comission.id });
      },
    });
  };

  const handleDeleteSelectedDepartments = async () => {
    const ok = await confirm({
      modalData: {
        text: "Вы уверены, что хотите удалить выбранные комиссии?",
      },
    });
    if (!ok) return;

    for (const comission of selectedItems) {
      removeComission({ id: comission.id });
    }

    clearSelectedItems();
  };

  useEffect(() => {
    if (!isFetched && companyId !== null) {
      fetchComissions(companyId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tableBodyContent = comissions.length ? (
    comissions.map((comission) => {
      const isSelected = getIsItemSelected(comission);
      const modalData = {
        editMode: true,
        modalTitle: "Изменить комиссию",
        comissionId: comission.id,
        title: comission.title,
        order_number: comission.order_number,
        order_date: comission.order_date,
        chairman: comission.chairman,
        members: comission.members,
      };
      return (
        <tr key={comission.id}>
          <td>
            <span>
              <Checkbox
                color="primary"
                checked={isSelected}
                onChange={(e) => selectById(comission, e.target.checked)}
              />
            </span>
            <span
              className={style.wrapperRow}
              onClick={() =>
                open("CreateComissionModal", {
                  btnText: "Сохранить",
                  modalData: modalData,
                })
              }
            >
              <label
                htmlFor={`key`}
                className={clsx(
                  tableStyle.column_fixed_height,
                  style.wrapperLabel
                )}
              >
                <span className={style.wrapperLabel}>{comission.title}</span>
              </label>
            </span>
          </td>
          <td align="left">{comission.members.length}</td>
          <td>
            <div className={clsx(style.department_actions_col)}>
              {permissions.hasPermission(
                ACTIONS.educationalCenter_internalTraining_comissions_allowedToEdit
              ) && (
                <WrapperSVG color={"orange"}>
                  <Tooltip title="Изменить" placement="top">
                    <NotePencilIcon
                      size={24}
                      className={style.department_actions_col__icon}
                      onClick={() =>
                        open("CreateComissionModal", {
                          btnText: "Сохранить",
                          modalData: modalData,
                        })
                      }
                    />
                  </Tooltip>
                </WrapperSVG>
              )}
              {permissions.hasPermission(
                ACTIONS.educationalCenter_internalTraining_comissions_allowedToDelete
              ) && (
                <Tooltip title="Удалить" placement="top">
                  <TrashIcon
                    size={24}
                    className={style.department_actions_col__icon}
                    onClick={() => handleRemoveComission(comission)}
                  />
                </Tooltip>
              )}
            </div>
          </td>
        </tr>
      );
    })
  ) : (
    <tr>
      <td>
        <p style={{ textAlign: "center", width: "100%", margin: "24px 0px" }}>
          Комиссий нет
        </p>
      </td>
    </tr>
  );

  return (
    <div
      className={clsx(
        tableStyle.base_table_container,
        style.departments_table,
        style.no_border_top
      )}
    >
      {isLoading ? (
        <Loader />
      ) : error ? (
        <ErrorIndicator />
      ) : (
        <table className={clsx(tableStyle.base_table)}>
          <thead>
            <tr>
              <td>
                <span>
                  <Checkbox
                    color="primary"
                    checked={areAllItemsSelected}
                    onChange={(e) => selectAll(e.target.checked)}
                  />
                </span>
                <label className={clsx(tableStyle.checkbox_label)}>
                  Название комиссии
                </label>
              </td>
              <td align="left">Кол-во участников</td>
              <td align="right">
                {selectedItems.length > 0 && (
                  <span>
                    {permissions.hasPermission(
                      ACTIONS.educationalCenter_internalTraining_comissions_allowedToDelete
                    ) && (
                      <Tooltip title="Удалить" placement="top">
                        <TrashIcon
                          size={24}
                          className={style.department_actions_col__icon}
                          onClick={handleDeleteSelectedDepartments}
                        />
                      </Tooltip>
                    )}
                  </span>
                )}
              </td>
            </tr>
          </thead>
          <tbody>{tableBodyContent}</tbody>
        </table>
      )}
    </div>
  );
};

export default InternalStudyComissionsTable;
